import { CurrentUser } from "@rumble-it/common-lib/types/models/CurrentUser.interface";
import {
  dashboardNavItems,
  claimNavItems,
  DashboardRouteConfig,
} from "./dashboardNavItems";
import { ItemType, MenuItemType } from "antd/es/menu/interface";

export interface DashboardMenuConfig {
  activeItem: DashboardRouteConfig | undefined;
  menuItems: ItemType<MenuItemType>[];
  selectedKeys: string[];
  openKeys: string[];
}

export const dashboardMenuConfig = ({
  pathname,
  hasAdminRole,
  currentUser,
}: {
  pathname: string;
  hasAdminRole: boolean;
  currentUser: CurrentUser | null;
}): DashboardMenuConfig => {
  const userPermissions = currentUser?.permissions || [];
  const features = currentUser?.affiliateFeatureTypes || [];

  const baseNav = userPermissions.includes("admin_claim_direct_nav")
    ? claimNavItems
    : dashboardNavItems;

  const navItems = baseNav.filter((i) => {
    if (
      i.requiresRole &&
      !(i.requiresRole === "ROLE_OIDC_ADMIN" && hasAdminRole)
    ) {
      return false;
    }
    if (i.requiresPermissions && i.requiresPermissions.length > 0) {
      const hasAll = i.requiresPermissions.every((permission) =>
        userPermissions.includes(permission),
      );
      if (!hasAll) return false;
    }
    if (i.requiresFeatures && i.requiresFeatures.length > 0) {
      const hasAll = i.requiresFeatures.every((feature) =>
        features.includes(feature),
      );
      if (!hasAll) return false;
    }
    return true;
  });

  let activeItem: DashboardRouteConfig | undefined;
  let selectedKeys: string[] = [];
  let openKeys: string[] = [];

  for (const item of navItems) {
    if (item.children) {
      const activeChild = item.children.find((child) => child.match(pathname));
      if (activeChild) {
        activeItem = activeChild;
        selectedKeys = [activeChild.key];
        openKeys = [item.key];
        break;
      }

      for (const child of item.children) {
        if (child.children) {
          const activeGrandChild = child.children.find((grandChild) =>
            grandChild.match(pathname),
          );
          if (activeGrandChild) {
            activeItem = activeGrandChild;
            selectedKeys = [activeGrandChild.key];
            openKeys = [item.key, child.key];
            break;
          }
        }
      }

      if (activeItem) break;
    }

    if (item.match(pathname)) {
      activeItem = item;
      selectedKeys = [item.key];
      break;
    }
  }

  const menuItems = navItems.map((item) => ({
    key: item.key,
    icon: item.icon,
    label: item.label,
    children: item.children?.map((child) => ({
      key: child.key,
      icon: child.icon,
      label: child.label,
      children: child.children?.map((grandChild) => ({
        key: grandChild.key,
        icon: grandChild.icon,
        label: grandChild.label,
      })),
    })),
  }));

  return { activeItem, menuItems, selectedKeys, openKeys };
};
